// src/components/ModalNotaCredito.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';

const round2 = (n) => Number((Number(n || 0) || 0).toFixed(2));
const safeFilename = (s) =>
  String(s || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_');

const toDateInputValue = (value) => {
  if (!value) return '';

  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return isoMatch[0];

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function ModalNotaCredito({
  show,
  onClose,
  tipoMovimiento = 'C',
  idSeleccionado, // cliente
  facturasSeleccionadas = [], // [{ idfactura, numero_factura }]
  idpagoEditar = null,
  // Cuando guardas, descarga automáticamente el PDF de la NC
  descargarAlGuardar = true
}) {
  const esEdicion = Boolean(idpagoEditar);
  const [numeroNc, setNumeroNc] = useState('');
  const [fecha, setFecha] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [facturasEdit, setFacturasEdit] = useState([]);
  const [idClienteEdit, setIdClienteEdit] = useState(null);
  const [cargandoEdicion, setCargandoEdicion] = useState(false);
  const [fotosExistentes, setFotosExistentes] = useState([]);
  const facturasTrabajo = useMemo(
    () => (esEdicion ? facturasEdit : facturasSeleccionadas) || [],
    [esEdicion, facturasEdit, facturasSeleccionadas]
  );
  const idActor = esEdicion ? idClienteEdit || idSeleccionado : idSeleccionado;

  // ---- Totales cabecera (CLIENTE)
  const [credito, setCredito] = useState(0); // crédito del cliente
  const [flete, setFlete] = useState(0); // SOLO cliente
  const [otros, setOtros] = useState(0); // SOLO cliente
  const valorTotal = useMemo(
    () => round2(Number(credito || 0) + Number(flete || 0) + Number(otros || 0)),
    [credito, flete, otros]
  );

  // ===== NUEVO: Gastos por proveedor (NO afectan cliente) =====
  // { [proveedorId]: { flete_prov: number, otros_prov: number } }
  const [gastosProv, setGastosProv] = useState({});

  // Saldos de facturas seleccionadas (para validar crédito)
  const [saldoPorFactura, setSaldoPorFactura] = useState({});
  const saldoTotalSeleccion = useMemo(
    () =>
      (facturasTrabajo || []).reduce(
        (s, f) => s + Number(saldoPorFactura[f.idfactura] || 0),
        0
      ),
    [saldoPorFactura, facturasTrabajo]
  );
  const saldoFavorEstimado = useMemo(
    () => round2(Math.max(0, valorTotal - saldoTotalSeleccion)),
    [valorTotal, saldoTotalSeleccion]
  );

  const [ncLineas, setNcLineas] = useState([]);
  const [ncLoading, setNcLoading] = useState(false);

  // Opción: descargar PDF al guardar
  const [autoDescargar, setAutoDescargar] = useState(Boolean(descargarAlGuardar));

  // ===== Catálogos (labels) =====
  const [, setCatalogos] = useState({ producto: {}, variedad: {}, longitud: {} });
  const arrToMapById = (arr) =>
    (arr || []).reduce((acc, it) => {
      if (it && it.id != null) acc[String(it.id)] = it.valor ?? String(it.id);
      return acc;
    }, {});

  // ===== Helpers =====
  const moneyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    []
  );
  const fmt = (n) => moneyFormatter.format(Number(n || 0));

  const descargarNC = async (idpago, numeroDescarga = '') => {
    if (!idpago) return;
    try {
      const res = await api.get(`/api/cartera/reporte-nc/${idpago}`, {
        params: { lado: 'C' },
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NC_${safeFilename(numeroDescarga || numeroNc || idpago)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('No se pudo descargar PDF NC', e);
    }
  };

  // Fotos por proveedor
  function appendFotosPorProveedor(fd, fotosProveedor = {}) {
    Object.entries(fotosProveedor || {}).forEach(([provId, files]) => {
      (files || []).forEach((file) => {
        fd.append(`fotos_proveedor_${provId}[]`, file);
      });
    });
  }

  // ===== Init (fecha/obs y saldos de facturas) =====
  useEffect(() => {
    if (!show) return;
    let cancelado = false;
    setAutoDescargar(Boolean(descargarAlGuardar));

    if (esEdicion) return;

    setFotosProveedor({});
    setFotosExistentes([]);
    setFacturasEdit([]);
    setIdClienteEdit(null);
    setNumeroNc('');
    setFecha(new Date().toISOString().slice(0, 10));
    const nums = (facturasTrabajo || [])
      .map((f) => f.numero_factura)
      .filter(Boolean)
      .join(', ');
    setObservaciones(nums ? `Operación sobre factura(s) ${nums}` : '');

    setCredito(0);
    setFlete(0);
    setOtros(0);

    // ✅ nuevo: limpiar gastos por proveedor al abrir
    setGastosProv({});

    (async () => {
      try {
        const { data } = await api.get('/api/cartera/nota-credito/siguiente-consecutivo');
        if (!cancelado && data?.siguiente) {
          setNumeroNc((prev) => prev || String(data.siguiente));
        }
      } catch (e) {
        console.error('No se pudo sugerir consecutivo NC', e);
      }
    })();

    (async () => {
      try {
        const hoy = new Date().toISOString().slice(0, 10);
        const { data } = await api.get('/api/cartera', {
          params: {
            tipoMovimiento: 'C',
            idtercero: idActor,
            desde: '1900-01-01',
            hasta: hoy
          }
        });
        const m = {};
        (Array.isArray(data) ? data : []).forEach((r) => {
          if (r?.id != null) m[Number(r.id)] = Number(r.saldo || 0);
        });
        setSaldoPorFactura(m);
      } catch (e) {
        console.error('No se pudo cargar saldos de facturas', e);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [show, idActor, facturasTrabajo, descargarAlGuardar, esEdicion]);

  // ===== Cargar NC existente para edicion =====
  useEffect(() => {
    if (!show || !esEdicion || !idpagoEditar) return;

    let cancelado = false;
    const cargarEdicion = async () => {
      setCargandoEdicion(true);
      setNcLoading(true);
      try {
        const { data } = await api.get(`/api/cartera/nota-credito/${idpagoEditar}/editar`);
        if (cancelado) return;

        if (data?.puedeEditar === false) {
          alert(data?.motivoBloqueo || 'No se puede editar esta nota de credito.');
          onClose?.();
          return;
        }

        const pago = data?.pago || {};
        const facturas = Array.isArray(data?.facturas) ? data.facturas : [];
        const detalle = Array.isArray(data?.detalle) ? data.detalle : [];

        setIdClienteEdit(pago.idtercero || null);
        setNumeroNc(pago.numero_comprobante || String(pago.idpago || idpagoEditar || ''));
        setFecha(toDateInputValue(pago.fecha) || new Date().toISOString().slice(0, 10));
        setObservaciones(pago.observaciones || '');
        setCredito(Number(pago.nc_credito_items || 0));
        setFlete(Number(pago.nc_flete || 0));
        setOtros(Number(pago.nc_otros || 0));
        setFacturasEdit(facturas);

        const saldos = {};
        facturas.forEach((f) => {
          if (f?.idfactura != null) saldos[Number(f.idfactura)] = Number(f.saldo || 0);
        });
        setSaldoPorFactura(saldos);

        setNcLineas(detalle);
        setProveedorSel(detalle.find((r) => Number(r.proveedor_id || 0))?.proveedor_id || null);

        const gastos = {};
        (Array.isArray(data?.prov_gastos) ? data.prov_gastos : []).forEach((g) => {
          const pid = Number(g?.proveedor_id || 0);
          if (!pid) return;
          gastos[pid] = {
            flete_prov: Number(g.flete_prov || 0),
            otros_prov: Number(g.otros_prov || 0)
          };
        });
        setGastosProv(gastos);
        setFotosProveedor({});
        setFotosExistentes(Array.isArray(data?.fotos) ? data.fotos : []);
      } catch (e) {
        console.error(e);
        alert(e?.response?.data?.error || 'No se pudo cargar la nota de credito.');
        onClose?.();
      } finally {
        if (!cancelado) {
          setCargandoEdicion(false);
          setNcLoading(false);
        }
      }
    };

    cargarEdicion();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, esEdicion, idpagoEditar]);

  // ===== Cargar detalle consolidado =====
  useEffect(() => {
    const cargar = async () => {
      if (esEdicion) return;
      const ids = facturasTrabajo.map((f) => f.idfactura).filter(Boolean);
      if (!ids.length) {
        setNcLineas([]);
        return;
      }
      setNcLoading(true);
      try {
        const [prod, varz, longi] = await Promise.all([
          api.get('/api/catalogo', { params: { categoria: 'producto' } }),
          api.get('/api/catalogo', { params: { categoria: 'variedad' } }),
          api.get('/api/catalogo', { params: { categoria: 'longitud' } })
        ]);
        const cats = {
          producto: arrToMapById(prod.data),
          variedad: arrToMapById(varz.data),
          longitud: arrToMapById(longi.data)
        };
        setCatalogos(cats);

        const { data } = await api.get('/api/facturas/consolidada-detalle', {
          params: { ids: ids.join(',') }
        });
        const lineas = Array.isArray(data) ? data : [];

        const rows = lineas.map((r) => {
          const idproducto = r.idproducto ?? r.producto ?? null;
          const idvariedad = r.idvariedad ?? r.variedad ?? null;
          const idlongitud = r.idlongitud ?? r.longitud ?? null;
          const cantidad_tallos =
            Number(
              r.cantidad_tallos ??
                r.tallos ??
                r.stems ??
                r.qty_stems ??
                r.cant_tallos ??
                r.cantidad ??
                0
            ) || 0;

          const sub = Number(r.subtotal || 0);

          // 💰 Precio unitario desde la factura
          const precio_unitario =
            r.precio_unitario != null
              ? Number(r.precio_unitario || 0)
              : cantidad_tallos > 0
                ? sub / cantidad_tallos
                : 0;

          return {
            iddetalle: r.iddetalle,
            idfactura: Number(r.idfactura),
            proveedor_id: Number(r.proveedor_id || 0),
            proveedor: r.proveedor || `Prov ${r.proveedor_id ?? ''}`,

            // ids reales
            producto: idproducto,
            variedad: idvariedad,
            longitud: idlongitud,

            // labels
            productoNom: cats.producto[String(idproducto)] ?? String(idproducto ?? ''),
            variedadNom: cats.variedad[String(idvariedad)] ?? String(idvariedad ?? ''),
            longitudNom: cats.longitud[String(idlongitud)] ?? String(idlongitud ?? ''),

            documento_proveedor: String(r.documento_proveedor || '').trim() || '—',
            subtotal: sub,
            precio_unitario,

            // montos editables
            saldo_linea: sub,
            max_credito: sub,
            cantidad_tallos,
            tallos_reclamo: 0,
            monto_credito: 0,
            motivo: ''
          };
        });

        setNcLineas(rows);
        if (rows.length) setProveedorSel(rows[0].proveedor_id || null);
      } catch (e) {
        console.error(e);
        alert('❌ No se pudo cargar el detalle de la factura consolidada');
      } finally {
        setNcLoading(false);
      }
    };

    if (show) cargar();
    if (!show) setNcLineas([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, facturasTrabajo, esEdicion]);

  // ===== Lista de proveedores / filtro =====
  const proveedores = useMemo(() => {
    const map = new Map();
    for (const r of ncLineas) {
      const id = Number(r.proveedor_id || 0);
      if (!id) continue;
      if (!map.has(id)) map.set(id, { id, nombre: r.proveedor || `Prov ${id}` });
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.nombre).localeCompare(String(b.nombre))
    );
  }, [ncLineas]);

  const [proveedorSel, setProveedorSel] = useState(null);

  // ==== Imágenes por proveedor ====
  const [fotosProveedor, setFotosProveedor] = useState({});
  const fileInputRef = useRef(null);
  const triggerUpload = () => proveedorSel && fileInputRef.current?.click();
  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !proveedorSel) return;
    setFotosProveedor((prev) => ({
      ...prev,
      [proveedorSel]: [...(prev[proveedorSel] || []), ...files]
    }));
    e.target.value = '';
  };
  const borrarFoto = (provId, idx) => {
    setFotosProveedor((prev) => {
      const arr = [...(prev[provId] || [])];
      arr.splice(idx, 1);
      return { ...prev, [provId]: arr };
    });
  };

  // ===== NUEVO: helpers de gastos proveedor =====
  const getProvGastos = (provId) => {
    const p = Number(provId || 0);
    const g = (p && gastosProv && gastosProv[p]) || null;
    return {
      flete_prov: Number(g?.flete_prov || 0),
      otros_prov: Number(g?.otros_prov || 0)
    };
  };

  const setProvGasto = (provId, campo, valor) => {
    const p = Number(provId || 0);
    if (!p) return;
    const v = Math.max(0, Number(valor || 0));
    setGastosProv((prev) => ({
      ...prev,
      [p]: {
        ...(prev?.[p] || {}),
        [campo]: v
      }
    }));
  };

  const filasProveedor = useMemo(
    () => ncLineas.filter((r) => !proveedorSel || r.proveedor_id === proveedorSel),
    [ncLineas, proveedorSel]
  );

  const totalAsignadoProveedor = useMemo(
    () => filasProveedor.reduce((s, r) => s + Number(r.monto_credito || 0), 0),
    [filasProveedor]
  );

  const totalProveedorConGastos = useMemo(() => {
    if (!proveedorSel) return Number(totalAsignadoProveedor || 0);
    const g = getProvGastos(proveedorSel);
    return Number(
      (Number(totalAsignadoProveedor || 0) + Number(g.flete_prov) + Number(g.otros_prov)).toFixed(2)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorSel, totalAsignadoProveedor, gastosProv]);

  // ===== Guardar =====
  const guardar = async () => {
    try {
      if (!fecha) {
        alert('Ingresa fecha');
        return;
      }

      const fleteNum = Number(flete || 0);
      const otrosNum = Number(otros || 0);
      const detalle_nc = ncLineas
        .filter((r) => Number(r.monto_credito) > 0)
        .map((r) => {
          const monto = round2(r.monto_credito);
          const tallosActuales = Number(r.tallos_reclamo || 0);
          const tallosLinea = Number(r.cantidad_tallos || 0);
          return {
            iddetalle: r.iddetalle,
            proveedor_id: r.proveedor_id,
            motivo: r.motivo || '',
            monto,
            producto: r.producto,
            variedad: r.variedad,
            longitud: r.longitud,
            documento_proveedor: r.documento_proveedor,
            tallos_reclamo: tallosActuales > 0 ? tallosActuales : tallosLinea
          };
        });
      const creditoClienteNum = round2(credito);
      const creditoProveedorNum = round2(
        detalle_nc.reduce((s, r) => s + Number(r.monto || 0), 0)
      );

      // Total del CLIENTE (NO incluye gastos por proveedor)
      const totalAplicar = round2(creditoClienteNum + fleteNum + otrosNum);

      if (!(totalAplicar > 0)) {
        alert('El total del cliente debe ser > 0');
        return;
      }

      if (!detalle_nc.length || !(creditoProveedorNum > 0)) {
        alert('Ingresa al menos un crédito en el detalle del proveedor.');
        return;
      }

      // 👇 Detalle por líneas (items)
      // ✅ NUEVO: gastos por proveedor (se guardarán en cartera_nc_detalle)
      const prov_gastos = Object.entries(gastosProv || {})
        .map(([provId, g]) => ({
          proveedor_id: Number(provId),
          flete_prov: Number(g?.flete_prov || 0),
          otros_prov: Number(g?.otros_prov || 0)
        }))
        .filter((x) => x.proveedor_id && (x.flete_prov > 0 || x.otros_prov > 0));

      // Reparto de total CLIENTE contra facturas (greedy)
      const ids = facturasTrabajo.map((f) => f.idfactura).filter(Boolean);
      if (!ids.length) {
        alert('Debes seleccionar al menos una factura.');
        return;
      }

      let restante = totalAplicar;
      const facturas = [];

      for (const id of ids) {
        if (restante <= 0) break;
        const saldo = Number(saldoPorFactura[id] || 0);
        if (saldo <= 0) continue;
        const usar = Math.min(restante, saldo);
        facturas.push({ idfactura: id, valorpago: usar });
        restante = Math.max(0, restante - usar);
      }

      const saldoFavorGenerado = round2(restante);

      const fd = new FormData();
      const payload = {
        tipoMovimiento: esEdicion ? 'C' : tipoMovimiento, // 'C'
        tipoDocumento: 'NC',
        idtercero: idActor,
        numero_comprobante: (numeroNc || '').trim() || null,
        fecha,

        // ✅ Total cliente (header)
        valorTotal: totalAplicar,
        observaciones,
        facturas,

        // detalle items
        detalle_nc,

        // ✅ Persistir en pagos (cliente)
        flete: fleteNum,
        otros: otrosNum,
        credito_items: creditoClienteNum,

        // ✅ NUEVO: por proveedor (para guardar en cartera_nc_detalle)
        prov_gastos
      };

      fd.append('payload', JSON.stringify(payload));
      appendFotosPorProveedor(fd, fotosProveedor);

      const { data } = esEdicion
        ? await api.put(`/api/cartera/nota-credito/${idpagoEditar}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        : await api.post('/api/cartera/pago-completo-nc', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

      const idpago = data?.idpago;
      if (autoDescargar && idpago) {
        await descargarNC(idpago, data?.numero_comprobante);
      }

      const mensajeSaldo =
        saldoFavorGenerado > 0
          ? `\nSaldo a favor disponible para el cliente: ${fmt(saldoFavorGenerado)}.`
          : '';
      alert(
        `${esEdicion ? 'Nota de credito actualizada.' : 'Nota de credito registrada.'}${mensajeSaldo}`
      );
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Error al guardar la Nota de Credito');
    }
  };

  // ===== UX modal =====
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  const fotosExistentesProveedor = useMemo(
    () =>
      proveedorSel
        ? fotosExistentes.filter((f) => Number(f.proveedor_id || 0) === Number(proveedorSel))
        : [],
    [fotosExistentes, proveedorSel]
  );

  if (!show) return null;

  const tituloFacturas = (facturasTrabajo || [])
    .map((f) => f.numero_factura)
    .filter(Boolean)
    .join(', ');

  // valores del proveedor seleccionado (para inputs)
  const gp = proveedorSel ? getProvGastos(proveedorSel) : { flete_prov: 0, otros_prov: 0 };

  return createPortal(
    <div
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div style={cardStyle} onMouseDown={(e) => e.stopPropagation()}>
        {/* Título con número(s) de factura */}
        <h3 style={{ marginBottom: 6 }}>
          {esEdicion
            ? `Editar Nota de Credito #${numeroNc || idpagoEditar}`
            : 'Nota de Credito (Cliente)'}{' '}
          {tituloFacturas && (
            <span style={{ fontWeight: 800, marginLeft: 8 }}>• Factura(s): {tituloFacturas}</span>
          )}
        </h3>
        {cargandoEdicion && (
          <div style={{ marginBottom: 8, color: '#555', fontSize: 13 }}>
            Cargando nota de credito...
          </div>
        )}

        {/* Cabecera compacta en una sola línea */}
        <div style={headRow}>
          <label style={lbl}>
            Fecha
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              style={inSm}
            />
          </label>

          <label style={{ ...lblInline, width: 170 }}>
            Consecutivo NC
            <input
              type="text"
              inputMode="numeric"
              maxLength={50}
              value={numeroNc}
              onChange={(e) => setNumeroNc(e.target.value)}
              placeholder="Auto"
              style={inSm}
            />
          </label>

          <label style={lblInline}>
            Crédito
            <input
              type="number"
              step="0.01"
              min="0"
              value={credito}
              onChange={(e) => setCredito(e.target.value)}
              style={inSm}
              title={`Crédito del cliente. Saldo disponible: ${fmt(saldoTotalSeleccion)}`}
            />
          </label>

          <label style={lblInline}>
            Flete
            <input
              type="number"
              step="0.01"
              min="0"
              value={flete}
              onChange={(e) => setFlete(e.target.value)}
              style={inSm}
            />
          </label>

          <label style={lblInline}>
            Otros
            <input
              type="number"
              step="0.01"
              min="0"
              value={otros}
              onChange={(e) => setOtros(e.target.value)}
              style={inSm}
            />
          </label>

          <div style={totalBox}>
            <div>
              Total cliente:&nbsp;<b>{fmt(valorTotal)}</b>
            </div>
            {saldoFavorEstimado > 0 && (
              <div style={{ color: '#19733d', marginTop: 3 }}>
                Saldo a favor:&nbsp;<b>{fmt(saldoFavorEstimado)}</b>
              </div>
            )}
          </div>
        </div>

        {/* Observaciones */}
        <div
          style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}
        >
          <label style={{ ...lbl, margin: 0, flex: '1 1 420px' }}>
            Observaciones
            <input
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              style={inFull}
            />
          </label>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '260px 1fr',
            gap: 12,
            marginTop: 10,
            minHeight: 460
          }}
        >
          {/* Proveedores */}
          <div style={leftPane}>
            <div style={{ fontWeight: 600, margin: '0 0 6px 0' }}>Proveedores</div>
            {proveedores.length === 0 && <div style={{ opacity: 0.6 }}>Sin proveedores</div>}
            <ul style={provListUl}>
              {proveedores.map((p) => (
                <li
                  key={p.id}
                  onClick={() => setProveedorSel(p.id)}
                  style={{
                    ...provItem,
                    ...(p.id === proveedorSel ? provItemSel : null)
                  }}
                  title={p.nombre}
                >
                  {p.nombre}
                </li>
              ))}
            </ul>
          </div>

          {/* Detalle por proveedor */}
          <div style={rightPane}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <strong>Detalle de compra del proveedor seleccionado</strong>

              <div style={{ marginLeft: 'auto', fontSize: 13, textAlign: 'right' }}>
                Crédito prov (items): <b>{fmt(totalAsignadoProveedor)}</b>
                <span style={{ margin: '0 8px', opacity: 0.6 }}>|</span>
                Total prov (items + flete/otros): <b>{fmt(totalProveedorConGastos)}</b>
                <span style={{ margin: '0 8px', opacity: 0.6 }}>|</span>
                Crédito cliente: <b>{fmt(credito)}</b>
              </div>
            </div>

            {/* Botón subir imágenes */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                marginBottom: 6,
                flexWrap: 'wrap'
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onFilesPicked}
              />
              <button onClick={triggerUpload} disabled={!proveedorSel}>
                📷 Subir imágenes (proveedor)
              </button>

              {proveedorSel && (fotosProveedor[proveedorSel]?.length || 0) > 0 && (
                <span style={{ fontSize: 12, color: '#555' }}>
                  {fotosProveedor[proveedorSel].length} archivo(s)
                </span>
              )}

              {proveedorSel && fotosExistentesProveedor.length > 0 && (
                <span style={{ fontSize: 12, color: '#555' }}>
                  {fotosExistentesProveedor.length} imagen(es) guardada(s)
                </span>
              )}

              {/* ✅ NUEVO: flete/otros del proveedor (no cliente) */}
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
              >
                <label style={{ ...lblInline, width: 160, margin: 0 }}>
                  Flete prov
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={gp.flete_prov}
                    onChange={(e) => setProvGasto(proveedorSel, 'flete_prov', e.target.value)}
                    style={{ ...inSm, width: 150, textAlign: 'right' }}
                    disabled={!proveedorSel}
                  />
                </label>

                <label style={{ ...lblInline, width: 160, margin: 0 }}>
                  Otros prov
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={gp.otros_prov}
                    onChange={(e) => setProvGasto(proveedorSel, 'otros_prov', e.target.value)}
                    style={{ ...inSm, width: 150, textAlign: 'right' }}
                    disabled={!proveedorSel}
                  />
                </label>
              </div>
            </div>

            {ncLoading ? (
              'Cargando…'
            ) : (
              <div style={{ overflow: 'auto', maxHeight: 420 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thSm}>Doc Prov</th>
                      <th style={thSm}>Producto</th>
                      <th style={thSm}>Variedad</th>
                      <th style={thSm}>Long.</th>
                      <th style={thRightSm}>Saldo línea</th>
                      <th style={thRightSm}>Tallos</th>
                      <th style={thRightSm}>P. und</th>
                      <th style={thRightSm}>Tallos reclamo</th>
                      <th style={thSm}>Motivo</th>
                      <th style={thRightSm}>Crédito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filasProveedor.map((r) => (
                      <tr key={r.iddetalle} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={tdSm}>{r.documento_proveedor}</td>
                        <td style={tdSm}>{r.productoNom || '—'}</td>
                        <td style={tdSm}>{r.variedadNom || '—'}</td>
                        <td style={tdSm}>{r.longitudNom || '—'}</td>
                        <td style={tdRightSm}>{fmt(r.saldo_linea)}</td>
                        <td style={tdRightSm}>{Number(r.cantidad_tallos || 0)}</td>
                        <td style={tdRightSm}>{fmt(r.precio_unitario)}</td>
                        <td style={tdRightSm}>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            value={r.tallos_reclamo ?? 0}
                            onChange={(e) => {
                              const v = Math.max(0, parseInt(e.target.value || '0', 10) || 0);
                              setNcLineas((rows) =>
                                rows.map((x) => {
                                  if (x.iddetalle !== r.iddetalle) return x;
                                  const precio = Number(x.precio_unitario || 0);
                                  const max = Number(x.max_credito || 0);
                                  let monto = round2(v * precio);
                                  if (monto > max) monto = max; // no pasar del saldo de la línea
                                  return { ...x, tallos_reclamo: v, monto_credito: monto };
                                })
                              );
                            }}
                            style={inXs}
                          />
                        </td>
                        <td style={tdSm}>
                          <select
                            value={r.motivo}
                            onChange={(e) =>
                              setNcLineas((rows) =>
                                rows.map((x) =>
                                  x.iddetalle === r.iddetalle ? { ...x, motivo: e.target.value } : x
                                )
                              )
                            }
                            style={selXs}
                          >
                            <option value="">— Selecciona —</option>
                            <option value="Producto dañado">Producto dañado</option>
                            <option value="Falta de calidad">Falta de calidad</option>
                            <option value="Falta de peso">Falta de peso</option>
                            <option value="Error de facturación">Error de facturación</option>
                            <option value="Otro">Otro</option>
                          </select>
                        </td>
                        <td style={tdRightSm}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={r.max_credito ?? 0}
                            value={r.monto_credito ?? 0}
                            onChange={(e) => {
                              const v = round2(
                                Math.max(
                                  0,
                                  Math.min(
                                    Number(r.max_credito || 0),
                                    parseFloat(e.target.value || 0)
                                  )
                                )
                              );
                              setNcLineas((rows) =>
                                rows.map((x) => {
                                  if (x.iddetalle !== r.iddetalle) return x;
                                  const tallosActuales = Number(x.tallos_reclamo || 0);
                                  const tallosLinea = Number(x.cantidad_tallos || 0);
                                  return {
                                    ...x,
                                    monto_credito: v,
                                    tallos_reclamo:
                                      v > 0 && tallosActuales <= 0 && tallosLinea > 0
                                        ? tallosLinea
                                        : tallosActuales
                                  };
                                })
                              );
                            }}
                            style={inXs}
                          />
                        </td>
                      </tr>
                    ))}
                    {!filasProveedor.length && (
                      <tr>
                        <td colSpan={10} style={{ padding: 10, opacity: 0.6, textAlign: 'center' }}>
                          Selecciona un proveedor para ver su detalle.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {proveedorSel && (fotosProveedor[proveedorSel]?.length || 0) > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <b>Imágenes del proveedor seleccionado:</b>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                      {fotosProveedor[proveedorSel].map((f, idx) => (
                        <li key={`${f.name}-${idx}`}>
                          {f.name}{' '}
                          <button type="button" onClick={() => borrarFoto(proveedorSel, idx)}>
                            quitar
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginTop: 12
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={autoDescargar}
              onChange={(e) => setAutoDescargar(e.target.checked)}
            />
            Descargar PDF al guardar
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose}>Cancelar</button>
            <button onClick={guardar}>
              {esEdicion ? 'Actualizar Nota de Credito' : 'Guardar Nota de Credito'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ===== estilos ===== */
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.40)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '28px 20px',
  zIndex: 1300
};
const cardStyle = {
  width: '96vw',
  maxWidth: 1400,
  background: '#fff',
  borderRadius: 14,
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
  padding: 16,
  maxHeight: 'calc(100dvh - 56px)',
  overflow: 'auto'
};

/* Cabecera compacta */
const headRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap'
};
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const lblInline = { ...lbl, width: 140 };
const inSm = { height: 28, lineHeight: '28px', padding: '2px 8px', fontSize: 13 };
const inFull = { ...inSm, width: '100%' };
const totalBox = {
  marginLeft: 'auto',
  fontWeight: 700,
  fontSize: 14,
  padding: '2px 8px'
};

/* Paneles con scroll interno */
const leftPane = {
  overflow: 'auto',
  border: 'none',
  borderRadius: 8,
  padding: 0,
  maxHeight: 520
};
const rightPane = {
  border: '1px solid #ddd',
  borderRadius: 10,
  padding: 8,
  overflow: 'hidden'
};

/* Lista de proveedores (compacta) */
const provListUl = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  maxHeight: 480,
  overflow: 'auto',
  border: '1px solid #e6e6e6',
  borderRadius: 8
};
const provItem = {
  padding: '6px 10px',
  fontSize: 13,
  cursor: 'pointer',
  borderBottom: '1px solid #ececec',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};
const provItemSel = {
  background: '#e8f2ff',
  fontWeight: 600
};

/* Tabla compacta */
const thSm = {
  textAlign: 'left',
  padding: '6px 6px',
  borderBottom: '1px solid #ddd',
  position: 'sticky',
  top: 0,
  background: '#fafafa',
  zIndex: 1,
  fontSize: 12
};
const thRightSm = { ...thSm, textAlign: 'right' };
const tdSm = { padding: '4px 6px', fontSize: 13, verticalAlign: 'middle' };
const tdRightSm = { ...tdSm, textAlign: 'right' };

/* Inputs compactos dentro de la grilla */
const inXs = {
  height: 26,
  lineHeight: '26px',
  padding: '2px 6px',
  fontSize: 13,
  width: 96,
  textAlign: 'right'
};
const selXs = { height: 26, padding: '2px 6px', fontSize: 13 };
